// Assists — League / Club — ranked horizontal bar chart (Phase 3).
//
// Second consumer of the ranked-bar type. Same thin-wrapper shape as Top
// Scorers: resolve data + per-row presentation, hand off to <RankedBars>.
// Differs from Top Scorers only in presentation + one shaping flag:
//  - League: purple bars (inline #7242a9 over the `team` class), every label
//    white, fixture teams highlighted, and zero-assist players filtered out
//    (`shapeLeagueScorers(..., filterZero: true)`).
//  - Club: club-coloured bars, zero-assist players dropped — identical to Top
//    Scorers Club.

import { clubFromName, shapeLeagueScorers, shapeClubScorers, type ScorerVenue, type ScorerTimeline, type AssistsLeagueData, type AssistsClubData } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import RankedBars, { type RankedBarRow } from "./RankedBars.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface AssistsProps {
  /** League (whole division) vs Club (one team's providers). */
  variant: "league" | "club";
  fixture: string;
  /** Club only: selected team (full name); falls back to the fixture's home side. */
  team?: string | null;
  /** Venue toggle: "All" | "Home" | "Away". */
  ha?: string | null;
  /** Timeline toggle: "Season" | "5" | "10". */
  timeline?: string | null;
}

export default function Assists({
  variant,
  fixture,
  team,
  ha,
  timeline,
}: AssistsProps) {
  // League and Club are distinct endpoints; the bridge picks the variant.
  const key = variant === "league" ? "assists-league" : "assists-club";
  const { data, isPending, isError, refetch } = useWidgetData(
    key,
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const venue: ScorerVenue =
    ha === "Home" ? "Home" : ha === "Away" ? "Away" : "All";
  const tl: ScorerTimeline =
    timeline === "5" ? "5" : timeline === "10" ? "10" : "Season";

  let rows: RankedBarRow[];
  let max: number;

  if (variant === "league") {
    // `key` is "assists-league" here, so `data` is the league payload — assert
    // it, since the hook can't correlate the key with `variant`.
    const shape = shapeLeagueScorers(
      data as AssistsLeagueData,
      venue,
      tl,
      [t1, t2].filter(Boolean),
      true // drop zero-assist players
    );
    max = shape.max;
    // Purple bars (inline, over the `team` class); every label white.
    rows = shape.rows.map((r) => ({
      name: r.name,
      value: r.goals,
      selected: r.highlighted,
      bg: "#7242a9",
      labelColor: "#fff",
      countColor: "",
    }));
  } else {
    const selected = team || t1;
    const shape = shapeClubScorers(
      (data as AssistsClubData)[selected] ?? [],
      venue,
      tl
    );
    max = shape.max;
    const teamColour = clubFromName(selected)?.colour ?? "#2ecc71";
    rows = shape.rows.map((r) => ({
      name: r.name,
      value: r.goals,
      selected: false,
      bg: teamColour,
      labelColor: "",
      countColor: "",
    }));
  }

  return (
    <RankedBars rows={rows} max={max} barFillClass="bar-fill team bento-fill" />
  );
}
